import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { CartContext } from "../../context/CartContext";
import "./Sidebar.css";

const EcommerceSidebar = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profileExpanded, setProfileExpanded] = useState(false);

  const searchBoxRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const { totalItems } = useContext(CartContext);

  // Auth State
  const token = localStorage.getItem("token");
  const storedUser = JSON.parse(localStorage.getItem("user") || "null");
  const isAuthenticated = !!token && !!storedUser;

  const displayName = storedUser?.name?.trim() || "";
  const initials = displayName
    ? displayName.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase()
    : "G";

  // Reset states on route change
  useEffect(() => {
    setSidebarOpen(false);
    setShowSearchResults(false);
  }, [location.pathname]);

  // Keep submenu open on profile pages
  useEffect(() => {
    if (location.pathname.startsWith("/profile")) {
      setProfileExpanded(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Search Logic
  useEffect(() => {
    const fetchSearchResults = async () => {
      const query = searchTerm.trim();
      if (!query) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }
      try {
        setLoading(true);
        setShowSearchResults(true);
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL || "http://localhost:5000"}/api/products/search?q=${encodeURIComponent(query)}`
        );
        const data = await res.json();
        setSearchResults(Array.isArray(data) ? data : data.products || []);
      } catch (error) {
        console.error("Search error:", error);
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    };

    const delayDebounce = setTimeout(fetchSearchResults, 300);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const query = searchTerm.trim();
    if (!query) return;
    navigate(`/shop?search=${encodeURIComponent(query)}`);
    setShowSearchResults(false);
    setSidebarOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setSidebarOpen(false);
    navigate("/login");
  };

  return (
    <>
      <div className="sidebar-mobile-bar">
        <div className="sidebar-mobile-row">
          <button
            className="sidebar-menu-btn"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
          >
            <i className="fa-solid fa-bars-staggered"></i>
          </button>

          <Link to="/" className="sidebar-mobile-logo">
            <span>Versa</span>core
          </Link>

          <Link to="/cart" className="sidebar-mobile-cart" aria-label="Cart">
            <i className="fa-solid fa-bag-shopping"></i>
            {totalItems > 0 && <span>{totalItems}</span>}
          </Link>
        </div>

        <form className="mobile-bar-search" onSubmit={handleSubmit} ref={searchBoxRef}>
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => {
              if (searchTerm.trim()) setShowSearchResults(true);
            }}
          />
          <button type="submit" aria-label="Search">
            <i className="fa-solid fa-magnifying-glass"></i>
          </button>

          {showSearchResults && (
            <div className="mobile-search-dropdown">
              {loading ? (
                <div className="search-empty">Searching...</div>
              ) : searchResults.length > 0 ? (
                searchResults.map((product) => (
                  <Link
                    to={`/product/${product._id}`}
                    key={product._id}
                    className="search-item"
                    onClick={() => {
                      setShowSearchResults(false);
                      setSearchTerm("");
                    }}
                  >
                    <img src={product.image} alt={product.name} />
                    <div className="search-item-info">
                      <h4>{product.name}</h4>
                      <p>₹{product.price}</p>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="search-empty">No products found</div>
              )}
            </div>
          )}
        </form>
      </div>

      {/* Backdrop */}
      <div 
        className={`sidebar-backdrop ${sidebarOpen ? "open" : ""}`} 
        onClick={() => setSidebarOpen(false)} 
      />

      {/* Sidebar Drawer */}
      <aside className={`eco-sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="sidebar-header">
          <Link to="/" className="logo" onClick={() => setSidebarOpen(false)}>
            <span>Versa</span>core
          </Link>
          <button
            className="sidebar-close-btn"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
          >
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        {isAuthenticated ? (
          <Link to="/profile" className="sidebar-user-card" onClick={() => setSidebarOpen(false)}>
            <div className="sidebar-user-avatar">{initials}</div>
            <div className="sidebar-user-text">
              <h3>{displayName}</h3>
              <span>View Dashboard</span>
            </div>
          </Link>
        ) : (
          <div className="sidebar-auth-card">
            <p>Log in for the best experience</p>
            <div className="sidebar-auth-btns">
              <Link to="/login" className="s-btn-login" onClick={() => setSidebarOpen(false)}>Log In</Link>
              <Link to="/signup" className="s-btn-register" onClick={() => setSidebarOpen(false)}>Sign Up</Link>
            </div>
          </div>
        )}

        <nav className="sidebar-nav">
          <NavLink to="/" end onClick={() => setSidebarOpen(false)}>
            <i className="fa-solid fa-house"></i> Home
          </NavLink>
          <NavLink to="/shop" onClick={() => setSidebarOpen(false)}>
            <i className="fa-solid fa-store"></i> Shop
          </NavLink>
          <NavLink to="/new-arrivals" onClick={() => setSidebarOpen(false)}>
  <i className="fa-solid fa-wand-magic-sparkles"></i> New Arrivals
</NavLink>
          <NavLink to="/categories" onClick={() => setSidebarOpen(false)}>
            <i className="fa-solid fa-layer-group"></i> Categories
          </NavLink>
          <NavLink to="/deals" onClick={() => setSidebarOpen(false)}>
            <i className="fa-solid fa-tag"></i> Deals
          </NavLink>
        </nav>

        <div className="sidebar-divider" />

        <div className="sidebar-links">
          {isAuthenticated && (
            <div className={`sidebar-submenu ${profileExpanded ? "expanded" : ""}`}>
              <button
                className="sidebar-submenu-toggle"
                onClick={() => setProfileExpanded((prev) => !prev)}
              >
                <span className="sidebar-submenu-toggle-left">
                  <i className="fa-regular fa-user"></i> My Account
                </span>
                <i className={`fa-solid fa-chevron-${profileExpanded ? "up" : "down"} sidebar-submenu-chevron`}></i>
              </button>

              {profileExpanded && (
                <div className="sidebar-submenu-links">
                  <Link to="/profile?tab=orders" onClick={() => setSidebarOpen(false)}>
                    <i className="fa-solid fa-box"></i> Orders
                  </Link>
                  <Link to="/profile?tab=address" onClick={() => setSidebarOpen(false)}>
                    <i className="fa-solid fa-location-dot"></i> Address
                  </Link>
                  <Link to="/profile?tab=settings" onClick={() => setSidebarOpen(false)}>
                    <i className="fa-solid fa-gear"></i> Settings
                  </Link>
                </div>
              )}
            </div>
          )}

          <Link to="/wishlist" onClick={() => setSidebarOpen(false)}>
            <i className="fa-regular fa-heart"></i> Wishlist
          </Link>
          <Link to="/trackorder" onClick={() => setSidebarOpen(false)}>
            <i className="fa-solid fa-truck"></i> Track Order
          </Link>
          <Link to="/contact" onClick={() => setSidebarOpen(false)}>
            <i className="fa-solid fa-headset"></i> Support
          </Link>
          
          {isAuthenticated && (
            <button className="sidebar-logout-btn" onClick={handleLogout}>
              <i className="fa-solid fa-right-from-bracket"></i> Logout
            </button>
          )}
        </div>
      </aside>
    </>
  );
};

export default EcommerceSidebar;